import React, { ReactElement, useState } from 'react';
import Kendra from 'aws-sdk/clients/kendra';
import { attributeNameToFacetTitle } from '../utils';
import { FacetCheckbox } from './FacetCheckbox';
import './Facet.scss';
import { FacetValue } from './types';
import { SelectedFacetRetriever } from '../SelectedFacetRetriever';
import _ from 'lodash';
import { useTranslate } from 'src/i18n/useTranslate';

interface Props {
    attributeName: string;
    values: FacetValue[];
    disableAdd: boolean;

    facetConfiguration: {
        facetsToShowWhenUncollapsed: number;
        showCount: boolean;
    };

    onSelectionChange: (
        key: Kendra.DocumentAttributeKey,
        value: Kendra.DocumentAttributeValue,
        changeDetail: boolean
    ) => void;

    onClear: (key: Kendra.DocumentAttributeKey) => void;

    selectedFacets?: SelectedFacetRetriever;
}

export const Facet = (props: Props): ReactElement => {
    const t = useTranslate();
    const [expanded, setExpanded] = useState(false);
    const showLess = () => {
        setExpanded(false);
    };

    const showMore = () => {
        setExpanded(true);
    };

    const onClickClearButton = () => {
        props.onClear(props.attributeName);
    };

    const {
        attributeName,
        disableAdd,
        facetConfiguration,
        values,
        selectedFacets,
    } = props;

    const expandable =
        values.length > facetConfiguration.facetsToShowWhenUncollapsed;

    const hasSomeValuesSelected = !_.isEmpty(
        selectedFacets?.getStringSelectionsOf(attributeName)
    );

    const getFacetTitle = () => {
        const translationMap: {
            [key: string]: { key: string; fallback: string };
        } = {
            _category: { key: 'facet_category', fallback: 'Category' },
            _created_at: { key: 'facet_created_at', fallback: 'Created at' },
            _data_source_id: {
                key: 'facet_data_source',
                fallback: 'Data source',
            },
            _document_title: { key: 'facet_title', fallback: 'Title' },
            _file_type: { key: 'facet_file_type', fallback: 'File type' },
            _source_uri: { key: 'facet_source_uri', fallback: 'Source URI' },
            _last_updated_at: {
                key: 'facet_updated_at',
                fallback: 'Updated at',
            },
            _version: { key: 'facet_version', fallback: 'Version' },
            _view_count: { key: 'facet_view_count', fallback: 'View count' },
            documentName: {
                key: 'facet_document_name',
                fallback: 'Document Name',
            },
        };

        const translation = translationMap[attributeName];
        if (translation) {
            return t(translation.key, translation.fallback);
        }
        return attributeNameToFacetTitle(attributeName);
    };

    return (
        <div className="facet">
            <div className="facet-title">
                {getFacetTitle()}&nbsp;
                {hasSomeValuesSelected && (
                    <span onClick={onClickClearButton} className="action-link">
                        {t('clear', 'Clear')}
                    </span>
                )}
            </div>
            <div className="facet-value-container">
                {values.map((value, idx) => {
                    if (!value.DocumentAttributeValue) {
                        return null;
                    }

                    const show =
                        expanded ||
                        idx < facetConfiguration.facetsToShowWhenUncollapsed;

                    const isSelected = selectedFacets
                        ? selectedFacets.isSelected(
                              attributeName,
                              value.DocumentAttributeValue
                          )
                        : false;

                    if (show || isSelected) {
                        return (
                            <FacetCheckbox
                                key={idx}
                                attributeName={attributeName}
                                value={value.DocumentAttributeValue}
                                valueLabel={value.ValueLabel}
                                count={value.Count}
                                selected={isSelected}
                                disableAdd={disableAdd}
                                onSelectionChange={props.onSelectionChange}
                                facetConfiguration={facetConfiguration}
                            />
                        );
                    } else {
                        return null;
                    }
                })}
            </div>
            <div className="facet-footer">
                {expandable && expanded && (
                    <span className="action-link" onClick={showLess}>
                        {t('show_less', 'Show Less')}
                    </span>
                )}
                {expandable && !expanded && (
                    <span className="action-link" onClick={showMore}>
                        {t('show_more', 'Show More')}
                    </span>
                )}
            </div>
        </div>
    );
};
